import type {
  ScenarioAction,
  ScenarioChoice,
  ScenarioDocument,
  ScenarioLine,
  ScenarioNode,
  ScenarioResumePoint
} from '../app/types';

export interface ScenarioRunnerOptions {
  resumePoint?: ScenarioResumePoint;
  initialNodeId?: string;
  initialLineIndex?: number;
}

export interface ScenarioRunnerState {
  scenarioId: string;
  caseId: string;
  nodeId: string;
  lineIndex: number;
  lineId: string | null;
  atLastLine: boolean;
  hasChoices: boolean;
  nodeActionsExecuted: boolean;
}

export class ScenarioRunner {
  private readonly nodesById: Map<string, ScenarioNode>;
  private currentNode: ScenarioNode;
  private lineIndex: number;
  private nodeActionsExecuted = false;

  public constructor(
    private readonly scenario: ScenarioDocument,
    options: ScenarioRunnerOptions = {}
  ) {
    this.nodesById = createNodeMap(scenario);
    validateScenarioGraph(scenario, this.nodesById);

    const resumePoint = getApplicableResumePoint(scenario, options);
    const initialNodeId = options.initialNodeId ?? resumePoint?.nodeId ?? scenario.startNodeId;
    const initialNode = this.getRequiredNode(
      initialNodeId,
      getInitialNodeContext(scenario, options, resumePoint)
    );
    this.currentNode = initialNode;
    this.lineIndex = resolveInitialLineIndex(options, resumePoint, initialNode);
  }

  public getScenario(): ScenarioDocument {
    return this.scenario;
  }

  public getCurrentNode(): ScenarioNode {
    return this.currentNode;
  }

  public getCurrentLine(): ScenarioLine {
    const line = this.currentNode.lines[this.lineIndex];
    if (!line) {
      throw new Error(
        `Scenario "${this.scenario.id}" node "${this.currentNode.id}" has no line at index ${this.lineIndex}.`
      );
    }

    return line;
  }

  public getCurrentPosition(): ScenarioResumePoint {
    const line = this.getCurrentLine();
    const position: ScenarioResumePoint = {
      scenarioId: this.scenario.id,
      nodeId: this.currentNode.id,
      lineIndex: this.lineIndex
    };

    if (line.id) {
      position.lineId = line.id;
    }

    return position;
  }

  public getChoices(): readonly ScenarioChoice[] {
    return this.currentNode.choices ?? [];
  }

  public getNextNodeId(): string | null {
    return this.currentNode.next ?? null;
  }

  public canAdvanceLine(): boolean {
    return this.lineIndex < this.currentNode.lines.length - 1;
  }

  public advanceLine(): ScenarioLine {
    if (!this.canAdvanceLine()) {
      return this.getCurrentLine();
    }

    this.lineIndex += 1;
    return this.getCurrentLine();
  }

  public enterNode(nodeId: string, lineIndex = 0): ScenarioNode {
    const node = this.getRequiredNode(nodeId);
    this.currentNode = node;
    this.lineIndex = normalizeLineIndex(lineIndex, node);
    this.nodeActionsExecuted = false;
    return this.currentNode;
  }

  public moveToNextNode(): ScenarioNode | null {
    const nextNodeId = this.getNextNodeId();
    if (!nextNodeId) {
      return null;
    }

    return this.enterNode(nextNodeId);
  }

  public chooseChoice(choice: ScenarioChoice | string): ScenarioNode {
    const choices = this.getChoices();
    const targetChoice =
      typeof choice === 'string'
        ? choices.find((candidate) => candidate.id === choice || candidate.text === choice)
        : choices.find((candidate) => isSameChoice(candidate, choice));

    if (!targetChoice) {
      throw new Error(
        `Scenario "${this.scenario.id}" node "${this.currentNode.id}" does not contain choice ${describeChoice(choice)}.`
      );
    }

    return this.enterNode(targetChoice.next);
  }

  public consumeNodeActions(): readonly ScenarioAction[] {
    if (this.nodeActionsExecuted) {
      return [];
    }

    this.nodeActionsExecuted = true;
    return this.currentNode.actions ?? [];
  }

  public getState(): ScenarioRunnerState {
    const line = this.getCurrentLine();
    return {
      scenarioId: this.scenario.id,
      caseId: this.scenario.caseId,
      nodeId: this.currentNode.id,
      lineIndex: this.lineIndex,
      lineId: line.id ?? null,
      atLastLine: !this.canAdvanceLine(),
      hasChoices: this.getChoices().length > 0,
      nodeActionsExecuted: this.nodeActionsExecuted
    };
  }

  private getRequiredNode(nodeId: string, context = 'references'): ScenarioNode {
    const node = this.nodesById.get(nodeId);
    if (!node) {
      throw new Error(`Scenario "${this.scenario.id}" ${context} missing node "${nodeId}".`);
    }

    return node;
  }
}

function createNodeMap(scenario: ScenarioDocument): Map<string, ScenarioNode> {
  const nodesById = new Map<string, ScenarioNode>();
  for (const node of scenario.nodes) {
    if (nodesById.has(node.id)) {
      throw new Error(`Scenario "${scenario.id}" contains duplicate node "${node.id}".`);
    }

    nodesById.set(node.id, node);
  }

  return nodesById;
}

function validateScenarioGraph(
  scenario: ScenarioDocument,
  nodesById: ReadonlyMap<string, ScenarioNode>
): void {
  if (!nodesById.has(scenario.startNodeId)) {
    throw new Error(
      `Scenario "${scenario.id}" startNodeId "${scenario.startNodeId}" does not exist.`
    );
  }

  for (const node of scenario.nodes) {
    if (node.lines.length === 0) {
      throw new Error(
        `Scenario "${scenario.id}" node "${node.id}" must contain at least one line.`
      );
    }

    validateLineIds(scenario, node);
    validateChoiceIds(scenario, node);

    if (node.next !== undefined && node.next !== null && !nodesById.has(node.next)) {
      throw new Error(
        `Scenario "${scenario.id}" node "${node.id}" next references missing node "${node.next}".`
      );
    }

    for (const choice of node.choices ?? []) {
      if (!nodesById.has(choice.next)) {
        throw new Error(
          `Scenario "${scenario.id}" node "${node.id}" choice "${choice.text}" references missing node "${choice.next}".`
        );
      }
    }
  }
}

function validateLineIds(scenario: ScenarioDocument, node: ScenarioNode): void {
  const lineIds = new Set<string>();
  for (const line of node.lines) {
    if (!line.id) {
      continue;
    }

    if (lineIds.has(line.id)) {
      throw new Error(
        `Scenario "${scenario.id}" node "${node.id}" contains duplicate line "${line.id}".`
      );
    }

    lineIds.add(line.id);
  }
}

function validateChoiceIds(scenario: ScenarioDocument, node: ScenarioNode): void {
  const choiceIds = new Set<string>();
  for (const choice of node.choices ?? []) {
    if (!choice.id) {
      continue;
    }

    if (choiceIds.has(choice.id)) {
      throw new Error(
        `Scenario "${scenario.id}" node "${node.id}" contains duplicate choice "${choice.id}".`
      );
    }

    choiceIds.add(choice.id);
  }
}

function getApplicableResumePoint(
  scenario: ScenarioDocument,
  options: ScenarioRunnerOptions
): ScenarioResumePoint | undefined {
  if (options.initialNodeId || options.resumePoint?.scenarioId !== scenario.id) {
    return undefined;
  }

  return options.resumePoint;
}

function getInitialNodeContext(
  scenario: ScenarioDocument,
  options: ScenarioRunnerOptions,
  resumePoint: ScenarioResumePoint | undefined
): string {
  if (options.initialNodeId) {
    return 'initialNodeId references';
  }

  if (resumePoint) {
    return 'resumePoint references';
  }

  return `startNodeId "${scenario.startNodeId}" references`;
}

function resolveInitialLineIndex(
  options: ScenarioRunnerOptions,
  resumePoint: ScenarioResumePoint | undefined,
  node: ScenarioNode
): number {
  if (options.initialLineIndex !== undefined) {
    return normalizeLineIndex(options.initialLineIndex, node);
  }

  if (!resumePoint) {
    return 0;
  }

  if (resumePoint.lineId) {
    const lineIndex = node.lines.findIndex((line) => line.id === resumePoint.lineId);
    if (lineIndex >= 0) {
      return lineIndex;
    }
  }

  return normalizeLineIndex(resumePoint.lineIndex, node);
}

function normalizeLineIndex(lineIndex: number, node: ScenarioNode): number {
  if (!Number.isInteger(lineIndex) || lineIndex < 0) {
    return 0;
  }

  return Math.min(lineIndex, Math.max(0, node.lines.length - 1));
}

function isSameChoice(left: ScenarioChoice, right: ScenarioChoice): boolean {
  if (left === right) {
    return true;
  }

  const idMatches = Boolean(left.id && right.id && left.id === right.id);
  const contentMatches = left.text === right.text && left.next === right.next;

  return idMatches || contentMatches;
}

function describeChoice(choice: ScenarioChoice | string): string {
  if (typeof choice === 'string') {
    return `"${choice}"`;
  }

  const parts = [`text="${choice.text}"`, `next="${choice.next}"`];
  if (choice.id) {
    parts.unshift(`id="${choice.id}"`);
  }

  return `{ ${parts.join(' ')} }`;
}
